import React from "react";
import { Box, Typography } from "@mui/material";
import CloudUploadRoundedIcon from "@mui/icons-material/CloudUploadRounded";

type ImageDropzoneVariant =
  | "service"
  | "compact"
  | "inline"
  | "pair"
  | "thumb"
  | "card"
  | "cover";

interface ImageDropzoneProps {
  imageUrl?: string;
  onClick: () => void;
  title?: string;
  hint?: string;
  variant?: ImageDropzoneVariant;
}

const dropzoneStyles: Record<
  ImageDropzoneVariant,
  { box: object; preview: object; iconSize: "small" | "medium" }
> = {
  service: {
    box: {
      width: "100%",
      flex: 1,
      height: "100%",
      minHeight: { xs: 220, md: "100%" },
      aspectRatio: { xs: "1 / 1", md: "auto" },
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      gap: "8px",
      cursor: "pointer",
      border: "1px dashed #C9C3DE",
      background: "#F8F6FC",
      borderRadius: "12px",
      padding: "12px",
      color: "#5B2FD1",
      overflow: "hidden",
      boxSizing: "border-box",
      transition: "border-color 0.2s ease, background 0.2s ease",
      "&:hover": {
        borderColor: "#6C3CE9",
        background: "#F3EEFF",
      },
    },
    preview: {
      width: "100%",
      height: "100%",
      maxWidth: "100%",
      maxHeight: "100%",
      objectFit: "contain",
      borderRadius: "8px",
    },
    iconSize: "medium",
  },
  compact: {
    box: {
      width: "100%",
      height: "100%",
      minHeight: 96,
      flex: 1,
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      gap: "4px",
      cursor: "pointer",
      border: "1px dashed #C9C3DE",
      background: "#F8F6FC",
      borderRadius: "10px",
      padding: "8px",
      color: "#5B2FD1",
      overflow: "hidden",
      boxSizing: "border-box",
      transition: "border-color 0.2s ease, background 0.2s ease",
      "&:hover": {
        borderColor: "#6C3CE9",
        background: "#F3EEFF",
      },
    },
    preview: {
      width: "100%",
      height: "100%",
      maxWidth: "100%",
      maxHeight: "100%",
      objectFit: "contain",
      borderRadius: "6px",
    },
    iconSize: "small",
  },
  inline: {
    box: {
      width: 56,
      height: 56,
      minWidth: 56,
      minHeight: 56,
      flexShrink: 0,
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      cursor: "pointer",
      border: "1px dashed #C9C3DE",
      background: "#F8F6FC",
      borderRadius: "10px",
      padding: "4px",
      color: "#5B2FD1",
      overflow: "hidden",
      boxSizing: "border-box",
      transition: "border-color 0.2s ease, background 0.2s ease",
      "&:hover": {
        borderColor: "#6C3CE9",
        background: "#F3EEFF",
      },
    },
    preview: {
      width: "100%",
      height: "100%",
      objectFit: "cover",
      borderRadius: "6px",
    },
    iconSize: "small",
  },
  pair: {
    box: {
      width: "100%",
      flex: 1,
      height: 268,
      minHeight: 268,
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      gap: "8px",
      cursor: "pointer",
      border: "1px dashed #C9C3DE",
      background: "#F8F6FC",
      borderRadius: "12px",
      padding: "12px",
      color: "#5B2FD1",
      overflow: "hidden",
      boxSizing: "border-box",
      transition: "border-color 0.2s ease, background 0.2s ease",
      "&:hover": {
        borderColor: "#6C3CE9",
        background: "#F3EEFF",
      },
    },
    preview: {
      width: "100%",
      height: "100%",
      flex: 1,
      objectFit: "contain",
      borderRadius: "8px",
    },
    iconSize: "medium",
  },
  thumb: {
    box: {
      width: 120,
      height: 120,
      minWidth: 120,
      minHeight: 120,
      flexShrink: 0,
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      cursor: "pointer",
      border: "1px dashed #C9C3DE",
      background: "#FFF",
      borderRadius: "10px",
      padding: "4px",
      color: "#5B2FD1",
      overflow: "hidden",
      boxSizing: "border-box",
      transition: "border-color 0.2s ease, background 0.2s ease",
      "&:hover": {
        borderColor: "#6C3CE9",
        background: "#F3EEFF",
      },
    },
    preview: {
      width: "100%",
      height: "100%",
      objectFit: "contain",
      borderRadius: "6px",
    },
    iconSize: "small",
  },
  card: {
    box: {
      width: "100%",
      height: "100%",
      minHeight: 132,
      flex: 1,
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      gap: "6px",
      cursor: "pointer",
      border: "1px dashed #C9C3DE",
      background: "transparent",
      borderRadius: "12px",
      padding: "8px",
      color: "#5B2FD1",
      overflow: "hidden",
      boxSizing: "border-box",
      transition: "border-color 0.2s ease, background 0.2s ease",
      "&:hover": {
        borderColor: "#6C3CE9",
        background: "#F3EEFF",
      },
    },
    preview: {
      width: "100%",
      height: "100%",
      minHeight: 116,
      flex: 1,
      objectFit: "contain",
      borderRadius: "8px",
    },
    iconSize: "medium",
  },
  // Matches mobile app service cover frame (163×229, object-fit: cover).
  cover: {
    box: {
      position: "relative",
      width: 163,
      height: 229,
      minWidth: 163,
      minHeight: 229,
      maxWidth: 163,
      maxHeight: 229,
      flexShrink: 0,
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      gap: "6px",
      cursor: "pointer",
      border: "1px dashed #C9C3DE",
      background: "#F8F6FC",
      borderRadius: "12px",
      padding: 0,
      color: "#5B2FD1",
      overflow: "hidden",
      boxSizing: "border-box",
      transition: "border-color 0.2s ease, background 0.2s ease",
      "&:hover": {
        borderColor: "#6C3CE9",
        background: "#F3EEFF",
      },
    },
    preview: {
      position: "absolute",
      inset: 0,
      width: "100%",
      height: "100%",
      objectFit: "cover",
      objectPosition: "center",
      display: "block",
    },
    iconSize: "medium",
  },
};

const ImageDropzone: React.FC<ImageDropzoneProps> = ({
  imageUrl,
  onClick,
  title = "Upload image",
  hint = "JPG or PNG",
  variant = "service",
}) => {
  const style = dropzoneStyles[variant];
  const iconOnly = variant === "inline" || variant === "thumb";
  const coverFilled = variant === "cover" && Boolean(imageUrl);

  return (
    <Box
      sx={{
        ...style.box,
        ...(coverFilled
          ? {
              border: "1px solid #D9D4E8",
              background: "#111",
              "&:hover": {
                borderColor: "#6C3CE9",
                background: "#111",
              },
            }
          : null),
      }}
      onClick={onClick}
    >
      {imageUrl ? (
        <Box
          component="img"
          alt="upload preview"
          src={imageUrl}
          sx={style.preview}
        />
      ) : iconOnly ? (
        <CloudUploadRoundedIcon fontSize={style.iconSize} />
      ) : (
        <>
          <CloudUploadRoundedIcon fontSize={style.iconSize} />
          <Typography
            variant={variant === "compact" || variant === "cover" ? "caption" : "body2"}
            fontWeight={600}
            textAlign="center"
            px={1}
          >
            {title}
          </Typography>
          {hint && (
            <Typography
              variant="caption"
              color="text.secondary"
              textAlign="center"
              px={1}
            >
              {hint}
            </Typography>
          )}
        </>
      )}
    </Box>
  );
};

export default ImageDropzone;
